/**
 * LocalStorage Utilities for Onboarding Card State
 * Provides helpers to manage the onboarding card state in localStorage
 */

const STORAGE_KEY_PREFIX = 'onboarding_card_state_';

/**
 * Get the localStorage key for a specific company
 */
const getStorageKey = (companyId: string): string => {
  return `${STORAGE_KEY_PREFIX}${companyId}`;
};

/**
 * Get onboarding card state from localStorage for a specific company
 * @param companyId - The company ID
 * @returns Card state ('open' | 'minimized' | 'closed') or null if not found
 */
export function getOnboardingCardStateFromStorage(
  companyId: string
): 'open' | 'minimized' | 'closed' | null {
  if (!companyId) return null;

  try {
    const storageKey = getStorageKey(companyId);
    const storedValue = localStorage.getItem(storageKey);

    if (
      storedValue === 'open' ||
      storedValue === 'minimized' ||
      storedValue === 'closed'
    ) {
      return storedValue;
    }

    return null;
  } catch (error) {
    console.error(
      'Error reading onboarding card state from localStorage:',
      error
    );
    return null;
  }
}

/**
 * Set onboarding card state in localStorage for a specific company
 * @param companyId - The company ID
 * @param cardState - The card state to store
 */
export function setOnboardingCardStateInStorage(
  companyId: string,
  cardState: 'open' | 'minimized' | 'closed'
): void {
  if (!companyId) return;

  try {
    const storageKey = getStorageKey(companyId);
    localStorage.setItem(storageKey, cardState);

    // Dispatch a custom event so other components can react to the change
    window.dispatchEvent(
      new CustomEvent('onboardingCardStateStorageChanged', {
        detail: { companyId, cardState },
      })
    );
  } catch (error) {
    console.error('Error saving onboarding card state to localStorage:', error);
  }
}

/**
 * Clear onboarding card state from localStorage for a specific company
 * Used when switching businesses to force a fresh fetch from backend
 * @param companyId - The company ID
 */
export function clearOnboardingCardStateFromStorage(companyId: string): void {
  if (!companyId) return;

  try {
    const storageKey = getStorageKey(companyId);
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error(
      'Error clearing onboarding card state from localStorage:',
      error
    );
  }
}

/**
 * Clear all onboarding card states from localStorage
 * Used for cleanup or when logging out
 */
export function clearAllOnboardingCardStates(): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(STORAGE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error(
      'Error clearing all onboarding card states from localStorage:',
      error
    );
  }
}
